import { Fragment, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatGBP } from "@/lib/format";
import { createCheckoutSession } from "@/lib/stripe-checkout.functions";
import { releaseOrderPayoutViaStripe, refundOrderViaStripe } from "@/lib/stripe-connect.functions";
import { confirmDeliveryAndAutoRelease } from "@/lib/order-actions.functions";
import { getOrderInvoiceUrl } from "@/lib/invoices.functions";
import { createShipment } from "@/lib/seller-shipping.functions";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { SHIPPING_OPTIONS, findShipping } from "@/lib/shipping";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CreditCard, Truck, Package, CheckCircle2, ImageOff, ShieldAlert,
  BanknoteArrowUp, AlertOctagon, ExternalLink, Clock, Download, HelpCircle, FileSpreadsheet,
  Printer, Loader2,

} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { downloadClickDropCsv, type ClickDropOrder } from "@/lib/click-and-drop";
import { BuyerReviewActions } from "@/components/buyer-review-actions";
import { BuyerAutoPayoutPanel } from "@/components/buyer-auto-payout-panel";
import { DisputeReportDialog } from "@/components/dispute-report-dialog";
import { SellerPayoutPausedPanel, orderShouldShowSellerPayoutPanel } from "@/components/seller-payout-paused-panel";
import { OrderTrackingTimeline } from "@/components/order-tracking-timeline";
import { useTranslation } from "@/i18n";




const orderSearchSchema = z.object({
  status: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/_authenticated/orders")({
  head: () => ({ meta: [{ title: "Orders — BidNova Auctions" }] }),
  validateSearch: zodValidator(orderSearchSchema),
  component: OrdersPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

type OrderRow = {
  id: string;
  auction_id: string;
  buyer_id: string;
  seller_id: string;
  amount_pence: number;
  commission_pence: number;
  seller_payout_pence: number;
  status: string;
  paid_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  delivery_confirmed_at: string | null;
  tracking_number: string | null;
  stripe_checkout_url: string | null;
  payout_released_at: string | null;
  payout_reference: string | null;
  stripe_transfer_id: string | null;
  has_active_dispute: boolean;
  shipping_method: string | null;
  shipping_cost_pence: number | null;
  exported_to_click_drop_at: string | null;
  royal_mail_label_url: string | null;
  royal_mail_order_identifier: string | null;
  royal_mail_last_error: string | null;
  royal_mail_last_attempt_at: string | null;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  shipping_address: ClickDropOrder["shipping_address"];
  created_at: string;
  purchase_type: "auction" | "buy_now" | null;
  auctions: { id: string; title: string; images: string[] | null; weight_grams: number | null } | null;
  disputes: {
    id: string;
    status: string;
    admin_resolution: string | null;
    created_at: string;
    updated_at: string | null;
    resolved_at: string | null;
  }[] | null;
};


const STATUS_TONE: Record<string, string> = {
  pending_payment: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  paid: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  ready_to_ship: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  exported_to_click_drop: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  shipped: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  delivered: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  released: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  refunded: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
  disputed: "bg-red-500/15 text-red-400 border-red-500/30",
};
const STATUS_LABEL: Record<string, string> = {
  pending_payment: "Awaiting payment",
  paid: "Paid — ready to ship",
  ready_to_ship: "Ready to ship",
  exported_to_click_drop: "Exported to Click & Drop",
  shipped: "Shipped",
  delivered: "Delivered",
  released: "Payout released",
  refunded: "Refunded",
  cancelled: "Cancelled",
  disputed: "Disputed",
};

function OrdersPage() {
  const { t: tPage } = useTranslation("commerce");
  const { user } = useAuth();
  const { status } = Route.useSearch();

  useEffect(() => {
    supabase.rpc("close_expired_auctions" as never).then(() => {});
    supabase.rpc("auto_mark_delivered" as never).then(() => {});
  }, []);

  const { isAdmin } = useRole();


  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-12">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">{tPage("page.eyebrow", "Orders")}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl"><span className="text-gold-gradient">{tPage("page.title", "Your orders")}</span></h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
          {tPage("page.subtitle", "Track payments, shipments and payouts. All amounts in GBP.")}
        </p>
      </header>

      <Tabs defaultValue={status ? "sales" : "purchases"} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="purchases">{tPage("tabs.purchases", "My purchases")}</TabsTrigger>
          <TabsTrigger value="sales">{tPage("tabs.sales", "My sales")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="all">{tPage("tabs.all", "All orders (admin)")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="purchases">
          <OrdersTable scope="buyer" userId={user?.id ?? null} isAdmin={false} statusFilter={status} />
        </TabsContent>
        <TabsContent value="sales">
          <OrdersTable scope="seller" userId={user?.id ?? null} isAdmin={false} statusFilter={status} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="all">
            <OrdersTable scope="admin" userId={user?.id ?? null} isAdmin statusFilter={status} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export function OrdersTable({
  scope, userId, isAdmin, statusFilter, purchaseTypeFilter,
}: { scope: "buyer" | "seller" | "admin"; userId: string | null; isAdmin: boolean; statusFilter?: string; purchaseTypeFilter?: "" | "auction" | "buy_now" }) {
  const { t } = useTranslation("commerce");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [shipping, setShipping] = useState<OrderRow | null>(null);
  const [tracking, setTracking] = useState("");
  const [disputeFor, setDisputeFor] = useState<OrderRow | null>(null);

  const [releaseFor, setReleaseFor] = useState<OrderRow | null>(null);
  const [payoutRef, setPayoutRef] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [detailFor, setDetailFor] = useState<OrderRow | null>(null);

  const [payFor, setPayFor] = useState<OrderRow | null>(null);
  const [payMethod, setPayMethod] = useState<string>(SHIPPING_OPTIONS[0].id);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [guideOpen, setGuideOpen] = useState(false);
  const [labelFor, setLabelFor] = useState<OrderRow | null>(null);
  const [labelOpts, setLabelOpts] = useState<{ weightGrams: number; service: string; packageFormat: string; lengthMm: number; widthMm: number; depthMm: number }>({
    weightGrams: 100, service: "royal_mail_tracked_48", packageFormat: "smallParcel",
    lengthMm: 0, widthMm: 0, depthMm: 0,
  });

  const createSession = useServerFn(createCheckoutSession);
  const releasePayoutFn = useServerFn(releaseOrderPayoutViaStripe);

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ["orders", scope, userId],
    enabled: !!userId,
    queryFn: async () => {
      let q = supabase
        .from("orders")
        .select("id,auction_id,buyer_id,seller_id,amount_pence,commission_pence,seller_payout_pence,status,paid_at,shipped_at,delivered_at,delivery_confirmed_at,tracking_number,stripe_checkout_url,payout_released_at,payout_reference,stripe_transfer_id,has_active_dispute,shipping_method,shipping_cost_pence,exported_to_click_drop_at,royal_mail_label_url,royal_mail_order_identifier,royal_mail_last_error,royal_mail_last_attempt_at,buyer_name,buyer_email,buyer_phone,shipping_address,created_at,purchase_type,auctions(id,title,images,weight_grams),disputes(id,status,admin_resolution,created_at,updated_at,resolved_at)")
        .order("created_at", { ascending: false }).limit(500);
      if (scope === "buyer") q = q.eq("buyer_id", userId!);
      else if (scope === "seller") q = q.eq("seller_id", userId!);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as OrderRow[];
    },
  });

  const sellerIds = useMemo(
    () => Array.from(new Set((orders ?? []).map((o) => o.seller_id))).filter(Boolean),
    [orders],
  );
  const { data: sellerStripeMap } = useQuery({
    queryKey: ["orders-seller-stripe", scope, sellerIds],
    enabled: isAdmin && sellerIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,stripe_account_id,stripe_payouts_enabled")
        .in("id", sellerIds);
      if (error) throw error;
      const map: Record<string, { accountId: string | null; payoutsEnabled: boolean }> = {};
      for (const p of data ?? []) {
        map[p.id] = {
          accountId: p.stripe_account_id ?? null,
          payoutsEnabled: !!p.stripe_payouts_enabled,
        };
      }
      return map;
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["orders"] });

  const pay = useMutation({
    mutationFn: async ({ orderId, shippingMethod }: { orderId: string; shippingMethod: string }) => {
      const res = await createSession({
        data: { orderId, origin: window.location.origin, shippingMethod: shippingMethod as never },
      });
      if (!res?.url) throw new Error("Could not start checkout");
      window.location.href = res.url;
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const ship = useMutation({
    mutationFn: async ({ orderId, trackingNumber }: { orderId: string; trackingNumber: string }) => {
      const { error } = await supabase.rpc(
        "mark_order_shipped" as never, { _order_id: orderId, _tracking: trackingNumber || null } as never,
      );
      if (error) throw error;
    },
    onSuccess: () => { toast.success(t("toast.shippedSuccess", "Marked as shipped — buyer notified")); setShipping(null); setTracking(""); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const invoiceFn = useServerFn(getOrderInvoiceUrl);
  const downloadInvoice = async (orderId: string) => {
    try {
      const res = await invoiceFn({ data: { orderId } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("toast.invoiceError", "Could not open invoice"));
    }
  };

  const confirmDeliveryFn = useServerFn(confirmDeliveryAndAutoRelease);
  const confirmDelivery = useMutation({
    mutationFn: async (orderId: string) => {
      return confirmDeliveryFn({ data: { orderId } });
    },
    onSuccess: (res) => {
      if (res?.payoutAttempted && res?.transferId) {
        toast.success(t("toast.deliveryReleased", "Delivery confirmed — payment released to seller"));
      } else if (res?.payoutAttempted) {
        toast.success(t("toast.deliveryProcessing", "Delivery confirmed — payment processing"));
      } else {
        toast.success(t("toast.deliveryConfirmed", "Delivery confirmed"));
      }
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["buyer-auto-payout"] });
    },
    onError: () => {
      // Never surface Stripe/internal errors to the buyer.
      toast.error(t("toast.deliveryError", "Could not confirm delivery. Please try again."));
    },
  });

  // Dispute opening is handled by <DisputeReportDialog /> (new dispute centre).


  const release = useMutation({
    mutationFn: async ({ orderId, notes }: { orderId: string; notes: string }) => {
      return releasePayoutFn({ data: { orderId, notes: notes || null } });
    },
    onSuccess: (r) => {
      toast.success(t("toast.stripeTransferCreated", "Stripe transfer created: {id}", { id: r.transferId ?? "" }));
      setReleaseFor(null); setPayoutRef(""); setPayoutNotes(""); invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });


  const markExported = useMutation({
    mutationFn: async (orderIds: string[]) => {
      const { error } = await supabase.rpc(
        "mark_order_exported_click_drop" as never,
        { _order_ids: orderIds } as never,
      );
      if (error) throw error;
    },
    onSuccess: () => { toast.success(t("toast.exportedMarked", "Marked as exported — enter tracking once labels are printed")); setSelected({}); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  type LabelOverrides = {
    weightGrams?: number;
    service?: string;
    packageFormat?: string;
    lengthMm?: number;
    widthMm?: number;
    depthMm?: number;
  };
  const createShipmentFn = useServerFn(createShipment);
  const buyLabel = useMutation({
    mutationFn: async ({ orderId, overrides }: { orderId: string; overrides?: LabelOverrides }) => {
      const data = await createShipmentFn({ data: { orderId, overrides } });
      return data as { trackingNumber: string | null; labelUrl: string | null; duplicate?: boolean };
    },
    onSuccess: (data) => {
      toast.success(
        data.duplicate
          ? t("toast.labelDuplicate", "Label already exists for this order")
          : t("toast.labelGenerated", "Royal Mail label generated — buyer emailed tracking"),
      );
      if (data.labelUrl) window.open(data.labelUrl, "_blank", "noopener,noreferrer");
      setLabelFor(null);
      invalidate();
    },
    onError: (e: Error) => toast.error(t("toast.labelFailed", "Label failed: {message}", { message: e.message })),
  });

  const downloadLabel = async (o: OrderRow) => {
    if (!o.royal_mail_label_url) return;
    const { data, error } = await supabase.storage
      .from("shipping-labels")
      .createSignedUrl(o.royal_mail_label_url, 60 * 5);
    if (error || !data?.signedUrl) {
      toast.error(t("toast.labelOpenError", "Could not open label: {message}", { message: error?.message ?? "unknown error" }));
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };


  const exportCsv = (rows: OrderRow[]) => {
    const missing = rows.filter((r) => !r.shipping_address?.address?.line1);
    if (missing.length > 0) {
      toast.warning(t("toast.missingAddressWarning", "{count} order(s) have no shipping address on file — CSV rows will be blank for those.", { count: missing.length }));
    }
    downloadClickDropCsv(rows as unknown as ClickDropOrder[]);
    markExported.mutate(rows.map((r) => r.id));
  };

  const isSellerScope = scope === "seller" || scope === "admin";
  const activeStatuses = statusFilter ? statusFilter.split(",").filter(Boolean) : [];
  let filteredOrders = activeStatuses.length > 0
    ? (orders ?? []).filter((o) => activeStatuses.includes(o.status))
    : (orders ?? []);
  if (purchaseTypeFilter) {
    filteredOrders = filteredOrders.filter((o) => (o.purchase_type ?? "auction") === purchaseTypeFilter);
  }
  const exportableSelected = filteredOrders.filter(
    (o) => selected[o.id] && (o.status === "paid" || o.status === "ready_to_ship" || o.status === "exported_to_click_drop")
      && (o.seller_id === userId || isAdmin),
  );

  if (error) {
    return (
      <div className="rounded-xl border border-destructive/40 p-6 text-sm text-destructive flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 shrink-0" />
        <span>{t("loadError", "Couldn't load orders: {message}", { message: error.message })}</span>
      </div>
    );
  }

  const canRelease = (o: OrderRow) => {
    if (o.status !== "delivered" || o.has_active_dispute) return false;
    if (o.stripe_transfer_id) return false;
    if (o.payout_released_at) return false;
    if (isAdmin) {
      const s = sellerStripeMap?.[o.seller_id];
      // While the seller-stripe query is loading we don't know yet — hide to avoid a failing click.
      if (!s || !s.accountId || !s.payoutsEnabled) return false;
    }
    if (o.delivery_confirmed_at) return true;
    if (!o.shipped_at) return false;
    return Date.now() > new Date(o.shipped_at).getTime() + 72 * 3600_000;
  };

  const colSpan = isSellerScope ? (isAdmin ? 7 : 6) : (isAdmin ? 5 : 4);

  return (
    <>
      {(activeStatuses.length > 0 || purchaseTypeFilter) && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("filtered", "Filtered:")}</span>
          {activeStatuses.map((s) => (
            <Badge key={s} variant="outline" className="capitalize">{STATUS_LABEL[s] ?? s}</Badge>
          ))}
          {purchaseTypeFilter && (
            <Badge variant="outline" className="capitalize border-gold/40 text-gold">
              {purchaseTypeFilter === "buy_now" ? t("purchaseType.buyNow", "Buy It Now") : t("purchaseType.auction", "Auction")}
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/orders", search: (prev: { status?: string }) => ({ ...prev, status: "" }) })}>
            {t("clearFilter", "Clear filter")}
          </Button>
        </div>
      )}
      {isSellerScope && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-surface-2/60 px-4 py-3">
          <div className="flex items-center gap-3 text-sm">
            <FileSpreadsheet className="h-4 w-4 text-violet-300" />
            <span className="text-muted-foreground">
              {t("clickDrop.banner", "Royal Mail Click & Drop — select paid orders, export the CSV, then upload it in Click & Drop to buy postage.")}
            </span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setGuideOpen(true)}>
              <HelpCircle className="h-4 w-4 mr-1" /> {t("clickDrop.howToImport", "How to import")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={exportableSelected.length === 0 || markExported.isPending}
              onClick={() => exportCsv(exportableSelected)}
            >
              <Download className="h-4 w-4 mr-1" />
              {t("clickDrop.exportSelected", "Export {count} selected to CSV", { count: exportableSelected.length || "" })}
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border/60 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {isSellerScope && <TableHead className="w-10"></TableHead>}
              <TableHead>{t("table.item", "Item")}</TableHead>
              <TableHead>{t("table.status", "Status")}</TableHead>
              {isAdmin && <TableHead>{t("table.type", "Type")}</TableHead>}
              {isSellerScope && <TableHead className="text-right">{t("table.payout", "Payout")}</TableHead>}
              <TableHead className="text-right">{t("table.total", "Total")}</TableHead>
              <TableHead className="text-right">{t("table.actions", "Actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={colSpan} className="text-center text-muted-foreground py-10">{t("table.loading", "Loading…")}</TableCell></TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-16">
                  {activeStatuses.length > 0
                    ? t("table.emptyFiltered", "No orders match the selected status filter.")
                    : scope === "buyer" ? t("table.emptyBuyer", "You haven't won any auctions yet.")
                    : scope === "seller" ? t("table.emptySeller", "You haven't sold any items yet.")
                    : t("table.emptyAdmin", "No orders on the platform yet.")}
                </TableCell>
              </TableRow>
            ) : filteredOrders.map((o) => {
              const auc = o.auctions;
              const isBuyer = o.buyer_id === userId;
              const isSeller = o.seller_id === userId;
              const canExport = (isSeller || isAdmin) && (o.status === "paid" || o.status === "ready_to_ship" || o.status === "exported_to_click_drop");
              return (
                <Fragment key={o.id}>
                <TableRow>
                  {isSellerScope && (
                    <TableCell>
                      {canExport ? (
                        <Checkbox
                          checked={!!selected[o.id]}
                          onCheckedChange={(v) => setSelected((s) => ({ ...s, [o.id]: !!v }))}
                          aria-label="Select order for CSV export"
                        />
                      ) : null}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {auc?.images?.[0] ? (
                        <img src={auc.images[0]} alt="" className="h-12 w-12 rounded object-cover" />
                      ) : (
                        <div className="h-12 w-12 rounded bg-surface-2 flex items-center justify-center">
                          <ImageOff className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <button
                          onClick={() => setDetailFor(o)}
                          className="font-medium truncate max-w-[240px] text-left hover:text-gold"
                        >
                          {auc?.title ?? "Auction"}
                        </button>
                        <p className="text-xs text-muted-foreground font-mono">#{o.id.slice(0, 8)}</p>
                        {o.tracking_number && (
                          <p className="text-xs text-muted-foreground">{t("table.tracking", "Tracking: {number}", { number: o.tracking_number })}</p>
                        )}
                        {isSeller && o.royal_mail_last_error && !o.royal_mail_label_url && (
                          <p className="text-xs text-red-400 max-w-[240px] truncate" title={o.royal_mail_last_error}>
                            {t("table.labelError", "Label error: {message}", { message: o.royal_mail_last_error })}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 items-start">
                      <Badge variant="outline" className={STATUS_TONE[o.status] ?? ""}>
                        {t(`status.${o.status}`, STATUS_LABEL[o.status] ?? o.status)}
                      </Badge>
                      {o.has_active_dispute && (
                        <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/30">
                          <AlertOctagon className="h-3 w-3 mr-1" /> {t("table.disputeActive", "Dispute active")}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          (o.purchase_type ?? "auction") === "buy_now"
                            ? "border-gold/40 bg-gold/10 text-gold"
                            : "border-border/60 text-muted-foreground"
                        }
                      >
                        {(o.purchase_type ?? "auction") === "buy_now" ? t("purchaseType.buyNow", "Buy It Now") : t("purchaseType.auction", "Auction")}
                      </Badge>
                    </TableCell>
                  )}
                  {(scope === "seller" || isAdmin) && (
                    <TableCell className="text-right text-muted-foreground">
                      {formatGBP(o.seller_payout_pence)}
                    </TableCell>
                  )}
                  <TableCell className="text-right font-display text-gold-gradient">
                    {formatGBP(o.amount_pence)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {isBuyer && o.status === "pending_payment" && (
                        <Button size="sm" className="bg-gold-gradient hover:opacity-90"
                          onClick={() => {
                            setPayMethod(o.shipping_method ?? SHIPPING_OPTIONS[0].id);
                            setPayFor(o);
                          }} disabled={pay.isPending}>
                          <CreditCard className="h-4 w-4 mr-1" /> {t("table.payNow", "Pay now")}
                        </Button>
                      )}
                      {["paid","ready_to_ship","exported_to_click_drop","shipped","delivered","released","refunded"].includes(o.status) && (
                        <Button size="sm" variant="outline" onClick={() => downloadInvoice(o.id)}>
                          <Download className="h-4 w-4 mr-1" /> {isSeller ? t("table.receiptSeller", "Sales invoice (PDF)") : t("table.receiptBuyer", "Receipt (PDF)")}
                        </Button>
                      )}
                      {canExport && (
                        <Button size="sm" variant="outline"
                          onClick={() => exportCsv([o])}
                          disabled={markExported.isPending}>
                          <Download className="h-4 w-4 mr-1" /> {t("table.clickDropCsv", "Click & Drop CSV")}
                        </Button>
                      )}
                      {isSeller && !o.shipped_at && !o.royal_mail_label_url
                        && (o.status === "paid" || o.status === "ready_to_ship" || o.status === "exported_to_click_drop") && (
                        <Button size="sm" variant="outline"
                          onClick={() => {
                            setLabelOpts({
                              weightGrams: o.auctions?.weight_grams ?? 100,
                              service: o.shipping_method ?? "royal_mail_tracked_48",
                              packageFormat: "smallParcel",
                              lengthMm: 0, widthMm: 0, depthMm: 0,
                            });
                            setLabelFor(o);
                          }}
                          disabled={buyLabel.isPending}>
                          {buyLabel.isPending && buyLabel.variables?.orderId === o.id
                            ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            : <Printer className="h-4 w-4 mr-1" />}
                          {o.royal_mail_last_error ? t("table.retryLabel", "Retry Royal Mail label") : t("table.buyLabel", "Buy Royal Mail label")}
                        </Button>
                      )}
                      {isSeller && o.royal_mail_label_url && (
                        <Button size="sm" variant="outline" onClick={() => downloadLabel(o)}>
                          <Download className="h-4 w-4 mr-1" /> {t("table.downloadLabel", "Download & print label")}
                        </Button>
                      )}
                      {o.tracking_number && (
                        <Button size="sm" variant="ghost" asChild>
                          <a
                            href={`https://www.royalmail.com/track-your-item#/tracking-results/${encodeURIComponent(o.tracking_number)}`}
                            target="_blank" rel="noreferrer">
                            <ExternalLink className="h-4 w-4 mr-1" /> {t("table.trackParcel", "Track parcel")}
                          </a>
                        </Button>
                      )}
                      {isSeller && (o.status === "paid" || o.status === "ready_to_ship" || o.status === "exported_to_click_drop") && (
                        <Button size="sm" className="bg-gold-gradient hover:opacity-90"
                          onClick={() => { setShipping(o); setTracking(o.tracking_number ?? ""); }}>
                          <Truck className="h-4 w-4 mr-1" /> {t("table.enterTracking", "Enter tracking & ship")}
                        </Button>
                      )}

                      {isBuyer && o.status === "shipped" && (
                        <Button size="sm" variant="outline"
                          onClick={() => confirmDelivery.mutate(o.id)}
                          disabled={confirmDelivery.isPending}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> {t("table.confirmDelivery", "Confirm delivery")}
                        </Button>
                      )}
                      {isBuyer && (
                        <BuyerReviewActions order={o} currentUserId={userId} />
                      )}
                      {isBuyer && !o.has_active_dispute &&
                        !["released","refunded","cancelled"].includes(o.status) &&
                        ["paid","ready_to_ship","exported_to_click_drop","shipped","delivered"].includes(o.status) && (
                        <Button size="sm" variant="ghost"
                          onClick={() => setDisputeFor(o)}>
                          <AlertOctagon className="h-4 w-4 mr-1" /> {t("table.reportProblem", "Report a problem")}
                        </Button>
                      )}
                      {(isBuyer || isSeller) && o.has_active_dispute && (
                        <Button size="sm" variant="outline" asChild>
                          <Link to="/disputes">
                            <AlertOctagon className="h-4 w-4 mr-1" /> {t("table.viewDispute", "View dispute")}
                          </Link>
                        </Button>
                      )}

                      {isAdmin && canRelease(o) && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white"
                          onClick={() => { setReleaseFor(o); setPayoutRef(""); setPayoutNotes(""); }}>
                          <BanknoteArrowUp className="h-4 w-4 mr-1" /> {t("table.releasePayout", "Release payout")}
                        </Button>
                      )}
                      {o.status === "released" && (
                        <span className="text-xs text-emerald-400 inline-flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> {t("table.paidOut", "Paid out")}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {isBuyer && (o.status === "delivered" || o.status === "released" || o.has_active_dispute) && (
                  <TableRow key={`${o.id}-panel`}>
                    <TableCell colSpan={colSpan} className="bg-surface-1/40">
                      <BuyerAutoPayoutPanel
                        orderId={o.id}
                        confirmDisabled={confirmDelivery.isPending}
                        submitting={confirmDelivery.isPending && confirmDelivery.variables === o.id}
                        onConfirmDelivery={() => confirmDelivery.mutate(o.id)}
                        onReportProblem={() => setDisputeFor(o)}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {(isSeller || isAdmin) && orderShouldShowSellerPayoutPanel(o) && (
                  <TableRow key={`${o.id}-seller-payout-panel`}>
                    <TableCell colSpan={colSpan} className="bg-surface-1/40">
                      <SellerPayoutPausedPanel order={o} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Shipment dialog */}
      <Dialog open={!!shipping} onOpenChange={(o) => { if (!o) { setShipping(null); setTracking(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dialogs.shipment.title", "Enter tracking & mark shipped")}</DialogTitle>
            <DialogDescription>
              {t("dialogs.shipment.description", "Paste the tracking number from your Royal Mail Click & Drop label. The buyer is emailed automatically once you save.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{shipping?.auctions?.title}</p>
            <Label htmlFor="tracking">{t("dialogs.shipment.trackingLabel", "Tracking number")}</Label>
            <Input id="tracking" value={tracking} onChange={(e) => setTracking(e.target.value)}
              placeholder={t("dialogs.shipment.trackingPlaceholder", "e.g. AB123456789GB")} autoFocus />
            <p className="text-xs text-muted-foreground">
              {t("dialogs.shipment.trackingHelp", "Required. Enter the tracking / reference number Royal Mail printed on the label.")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShipping(null); setTracking(""); }}>{t("dialogs.shipment.cancel", "Cancel")}</Button>
            <Button className="bg-gold-gradient hover:opacity-90"
              disabled={ship.isPending || tracking.trim().length < 4}
              onClick={() => shipping && ship.mutate({ orderId: shipping.id, trackingNumber: tracking.trim() })}>
              {t("dialogs.shipment.save", "Save tracking & notify buyer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Click & Drop guide */}
      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("dialogs.guide.title", "Royal Mail Click & Drop — how to import your CSV")}</DialogTitle>
            <DialogDescription>
              {t("dialogs.guide.description", "BidNova Auctions exports orders in the standard Click & Drop template so you can buy postage in a couple of minutes.")}
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm list-decimal pl-5 text-muted-foreground">
            <li>{t("dialogs.guide.step1", "Sign in at business.parcel.royalmail.com and open Orders → Import orders.")}</li>
            <li>{t("dialogs.guide.step2", "Choose CSV as the format and upload the file you just downloaded from BidNova Auctions.")}</li>
            <li>{t("dialogs.guide.step3", "On the field-mapping screen, pick BidNova Auctions from your saved mappings (Click & Drop remembers it after the first upload — our headers match their standard template).")}</li>
            <li>{t("dialogs.guide.step4", "Review the imported orders, tick the ones you want to ship, and click Apply postage. The service column already shows the shipping method the buyer paid for.")}</li>
            <li>{t("dialogs.guide.step5", "Print the labels, drop off at any Post Office or arrange collection, then come back here and click Enter tracking & ship — the buyer is emailed automatically.")}</li>
          </ol>
          <div className="rounded-md border border-border/60 bg-surface-2/60 p-3 text-xs text-muted-foreground">
            {t("dialogs.guide.tip", "Tip: bulk-select multiple paid orders and export a single CSV — Click & Drop will create every order in one import.")}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuideOpen(false)}>{t("dialogs.guide.gotIt", "Got it")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Royal Mail label options dialog */}
      <Dialog open={!!labelFor} onOpenChange={(o) => { if (!o) setLabelFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dialogs.label.title", "Create Royal Mail label")}</DialogTitle>
            <DialogDescription>
              {t("dialogs.label.description", "Edit the parcel details for this order. Defaults come from your shipping settings.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground truncate">{labelFor?.auctions?.title}</p>
            {labelFor?.royal_mail_last_error && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-300">
                <div className="font-medium mb-1">{t("dialogs.label.lastAttemptFailed", "Last attempt failed")}</div>
                <div className="break-words">{labelFor.royal_mail_last_error}</div>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t("dialogs.label.weight", "Weight (grams)")}</Label>
                <Input type="number" min={1} value={labelOpts.weightGrams}
                  onChange={(e) => setLabelOpts((s) => ({ ...s, weightGrams: Number(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>{t("dialogs.label.packageType", "Package type")}</Label>
                <Select value={labelOpts.packageFormat}
                  onValueChange={(v) => setLabelOpts((s) => ({ ...s, packageFormat: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="letter">{t("dialogs.label.packageTypes.letter", "Letter")}</SelectItem>
                    <SelectItem value="largeLetter">{t("dialogs.label.packageTypes.largeLetter", "Large Letter")}</SelectItem>
                    <SelectItem value="smallParcel">{t("dialogs.label.packageTypes.smallParcel", "Small Parcel")}</SelectItem>
                    <SelectItem value="mediumParcel">{t("dialogs.label.packageTypes.mediumParcel", "Medium Parcel")}</SelectItem>
                    <SelectItem value="parcel">{t("dialogs.label.packageTypes.parcel", "Parcel")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>{t("dialogs.label.service", "Shipping service")}</Label>
                <Select value={labelOpts.service}
                  onValueChange={(v) => setLabelOpts((s) => ({ ...s, service: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SHIPPING_OPTIONS.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("dialogs.label.length", "Length (mm)")}</Label>
                <Input type="number" min={0} value={labelOpts.lengthMm}
                  onChange={(e) => setLabelOpts((s) => ({ ...s, lengthMm: Number(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>{t("dialogs.label.width", "Width (mm)")}</Label>
                <Input type="number" min={0} value={labelOpts.widthMm}
                  onChange={(e) => setLabelOpts((s) => ({ ...s, widthMm: Number(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>{t("dialogs.label.depth", "Depth (mm)")}</Label>
                <Input type="number" min={0} value={labelOpts.depthMm}
                  onChange={(e) => setLabelOpts((s) => ({ ...s, depthMm: Number(e.target.value) || 0 }))} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("dialogs.label.dimensionsHint", "Dimensions are optional — leave at zero to use the package-type defaults.")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLabelFor(null)}>{t("dialogs.label.cancel", "Cancel")}</Button>
            <Button className="bg-gold-gradient hover:opacity-90"
              disabled={buyLabel.isPending || !labelFor || labelOpts.weightGrams <= 0}
              onClick={() => labelFor && buyLabel.mutate({
                orderId: labelFor.id,
                overrides: {
                  weightGrams: labelOpts.weightGrams,
                  service: labelOpts.service,
                  packageFormat: labelOpts.packageFormat,
                  lengthMm: labelOpts.lengthMm || undefined,
                  widthMm: labelOpts.widthMm || undefined,
                  depthMm: labelOpts.depthMm || undefined,
                },
              })}>
              {buyLabel.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />}
              {labelFor?.royal_mail_last_error ? t("dialogs.label.retry", "Retry") : t("dialogs.label.create", "Create label")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report a problem dialog (new dispute centre) */}
      <DisputeReportDialog
        orderId={disputeFor?.id ?? null}
        orderTitle={disputeFor?.auctions?.title ?? null}
        open={!!disputeFor}
        onOpenChange={(o: boolean) => { if (!o) setDisputeFor(null); }}
      />


      {/* Release payout dialog (Stripe Connect) */}
      <Dialog open={!!releaseFor} onOpenChange={(o) => { if (!o) setReleaseFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dialogs.release.title", "Release payout via Stripe Connect")}</DialogTitle>
            <DialogDescription>
              {releaseFor
                ? t("dialogs.release.description", "Release {amount} to seller via Stripe Connect?", { amount: formatGBP(releaseFor.seller_payout_pence) })
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReleaseFor(null)}>{t("dialogs.release.cancel", "Cancel")}</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white"
              disabled={release.isPending}
              onClick={() => releaseFor && release.mutate({ orderId: releaseFor.id, notes: "" })}>
              {release.isPending ? t("dialogs.release.releasing", "Releasing…") : t("dialogs.release.releaseButton", "Release via Stripe")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Order detail (timeline + audit + tracking) */}
      <Dialog open={!!detailFor} onOpenChange={(o) => { if (!o) setDetailFor(null); }}>
        <DialogContent className="max-w-2xl">
          {detailFor && <OrderDetail order={detailFor} isAdmin={isAdmin} />}
        </DialogContent>
      </Dialog>

      {/* Pay-now shipping selection */}
      <Dialog open={!!payFor} onOpenChange={(o) => { if (!o) setPayFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dialogs.pay.title", "Choose shipping")}</DialogTitle>
            <DialogDescription>{t("dialogs.pay.description", "Pick a Royal Mail service. Shipping is added to your Stripe payment.")}</DialogDescription>
          </DialogHeader>
          {payFor && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>{t("dialogs.pay.method", "Shipping method")}</Label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SHIPPING_OPTIONS.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.label} — {formatGBP(opt.costPence)} · {opt.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-lg border border-border/60 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span>{t("dialogs.pay.item", "Item")}</span><span>{formatGBP(payFor.amount_pence)}</span></div>
                <div className="flex justify-between">
                  <span>{t("dialogs.pay.shipping", "Shipping")}</span>
                  <span>{formatGBP(findShipping(payMethod)?.costPence ?? 0)}</span>
                </div>
                <div className="flex justify-between font-medium border-t border-border/60 pt-1 mt-1">
                  <span>{t("dialogs.pay.total", "Total")}</span>
                  <span className="text-gold-gradient font-display">
                    {formatGBP(payFor.amount_pence + (findShipping(payMethod)?.costPence ?? 0))}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayFor(null)}>{t("dialogs.pay.cancel", "Cancel")}</Button>
            <Button
              className="bg-gold-gradient hover:opacity-90"
              disabled={pay.isPending}
              onClick={() => payFor && pay.mutate({ orderId: payFor.id, shippingMethod: payMethod })}
            >
              {pay.isPending ? t("dialogs.pay.redirecting", "Redirecting…") : t("dialogs.pay.continue", "Continue to Stripe")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function OrderDetail({ order, isAdmin }: { order: OrderRow; isAdmin: boolean }) {
  const { t } = useTranslation("commerce");
  const qc = useQueryClient();
  const [resolving, setResolving] = useState<{ id: string; resolution: string } | null>(null);
  const [resolveNotes, setResolveNotes] = useState("");

  const refundFn = useServerFn(refundOrderViaStripe);
  const resolve = useMutation({
    mutationFn: async ({ id, resolution, notes }: { id: string; resolution: string; notes: string }) => {
      if (resolution === "resolved_refund") {
        return refundFn({ data: { orderId: order.id, disputeId: id, notes: notes || null } });
      }
      const { error } = await supabase.rpc(
        "resolve_dispute" as never,
        { _dispute_id: id, _resolution: resolution, _notes: notes || null } as never,
      );
      if (error) throw error;
      return { ok: true } as const;
    },
    onSuccess: () => {
      toast.success(t("toast.disputeResolved", "Dispute resolved"));
      setResolving(null); setResolveNotes("");
      qc.invalidateQueries({ queryKey: ["order-disputes", order.id] });
      qc.invalidateQueries({ queryKey: ["order-audit", order.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const { data: audit } = useQuery({
    queryKey: ["order-audit", order.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_audit_log")
        .select("id,event_type,actor_id,amount_pence,metadata,created_at")
        .eq("order_id", order.id).order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: disputes } = useQuery({
    queryKey: ["order-disputes", order.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("disputes")
        .select("id,opened_by,reason,status,resolution_notes,resolved_at,created_at")
        .eq("order_id", order.id).order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{order.auctions?.title ?? t("orderCentre.auctionItem", "Auction item")}</DialogTitle>
        <DialogDescription className="font-mono text-xs">#{order.id}</DialogDescription>
      </DialogHeader>
      <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
        {/* Timeline */}
        <section>
          <h3 className="text-sm font-medium mb-3">{t("orderDetail.status", "Order status")}</h3>
          <OrderTrackingTimeline order={order} />
        </section>


        {/* Shipment */}
        <section>
          <h3 className="text-sm font-medium mb-2">{t("orderDetail.shipment", "Shipment tracking")}</h3>
          <div className="rounded-md border border-border/60 p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("orderDetail.trackingNumber", "Tracking number")}</span>
              <span className="font-mono">{order.tracking_number ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("orderDetail.shippedAt", "Shipped at")}</span>
              <span>{order.shipped_at ? new Date(order.shipped_at).toLocaleString("en-GB") : "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("orderDetail.deliveryConfirmed", "Delivery confirmed by buyer")}</span>
              <span>{order.delivery_confirmed_at ? t("orderDetail.yes", "Yes") : t("orderDetail.no", "No")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("orderDetail.autoDeliverEligibility", "Auto-deliver eligibility")}</span>
              <span>
                {order.shipped_at
                  ? new Date(new Date(order.shipped_at).getTime() + 72 * 3600_000).toLocaleString("en-GB")
                  : "—"}
              </span>
            </div>
          </div>
        </section>

        {/* Payout */}
        <section>
          <h3 className="text-sm font-medium mb-2">{t("orderDetail.payout", "Payout")}</h3>
          <div className="rounded-md border border-border/60 p-3 text-sm grid grid-cols-2 gap-y-1">
            <span className="text-muted-foreground">{t("orderDetail.saleTotal", "Sale total")}</span>
            <span className="text-right">{formatGBP(order.amount_pence)}</span>
            <span className="text-muted-foreground">{t("orderDetail.commission", "Commission")}</span>
            <span className="text-right">− {formatGBP(order.commission_pence)}</span>
            <span className="font-medium">{t("orderDetail.sellerPayout", "Seller payout")}</span>
            <span className="text-right font-display text-gold-gradient">{formatGBP(order.seller_payout_pence)}</span>
            <span className="text-muted-foreground">{t("orderDetail.released", "Released")}</span>
            <span className="text-right">
              {order.payout_released_at ? new Date(order.payout_released_at).toLocaleString("en-GB") : t("orderDetail.notYet", "Not yet")}
            </span>
            {order.stripe_transfer_id && (<>
              <span className="text-muted-foreground">{t("orderDetail.stripeTransferId", "Stripe transfer ID")}</span>
              <span className="text-right font-mono text-xs break-all">{order.stripe_transfer_id}</span>
            </>)}

          </div>
        </section>

        {/* Disputes */}
        {(disputes?.length ?? 0) > 0 && (
          <section>
            <h3 className="text-sm font-medium mb-2">{t("orderDetail.disputes", "Disputes")}</h3>
            <div className="space-y-2">
              {disputes!.map((d) => (
                <div key={d.id} className="rounded-md border border-border/60 p-3 text-sm">
                  <div className="flex justify-between">
                    <Badge variant="outline" className={
                      d.status === "open" ? "bg-red-500/15 text-red-400 border-red-500/30"
                        : "bg-slate-500/15 text-slate-300 border-slate-500/30"
                    }>{d.status}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(d.created_at).toLocaleString("en-GB")}
                    </span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap">{d.reason}</p>
                  {d.resolution_notes && (
                    <p className="mt-2 text-muted-foreground border-t border-border/50 pt-2">
                      {t("orderDetail.adminNote", "Admin note: {note}", { note: d.resolution_notes })}
                    </p>
                  )}
                  {isAdmin && ["open","awaiting_seller","awaiting_buyer","under_review"].includes(d.status) && (
                    <div className="mt-3 pt-3 border-t border-border/50 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" asChild>
                        <Link to="/disputes/$disputeId" params={{ disputeId: d.id }}>
                          <ExternalLink className="h-4 w-4 mr-1" /> {t("orderDetail.openDisputeCase", "Open dispute case")}
                        </Link>
                      </Button>
                    </div>
                  )}

                </div>
              ))}
            </div>
          </section>
        )}

        {/* Admin resolve dialog */}
        <Dialog open={!!resolving} onOpenChange={(o) => { if (!o) { setResolving(null); setResolveNotes(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {resolving?.resolution === "resolved_refund" && t("orderDetail.resolveRefundTitle", "Refund the buyer")}
                {resolving?.resolution === "resolved_release" && t("orderDetail.resolveReleaseTitle", "Release funds to the seller")}
                {resolving?.resolution === "cancelled" && t("orderDetail.resolveDismissTitle", "Dismiss this dispute")}
              </DialogTitle>
              <DialogDescription>
                {resolving?.resolution === "resolved_refund" && t("orderDetail.resolveRefundDesc", "Marks the order as refunded and closes the dispute. The buyer will be notified.")}
                {resolving?.resolution === "resolved_release" && t("orderDetail.resolveReleaseDesc", "Closes the dispute. Payout can then be released once delivery conditions are met.")}
                {resolving?.resolution === "cancelled" && t("orderDetail.resolveDismissDesc", "Closes the dispute without changing the order.")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>{t("orderDetail.resolveNotesLabel", "Resolution notes (shared with both parties)")}</Label>
              <Textarea rows={4} value={resolveNotes} onChange={(e) => setResolveNotes(e.target.value)}
                placeholder={t("orderDetail.resolveNotesPlaceholder", "Explain the decision so both buyer and seller understand the outcome.")} />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setResolving(null)}>{t("orderDetail.cancel", "Cancel")}</Button>
              <Button className="bg-gold-gradient hover:opacity-90" disabled={resolve.isPending}
                onClick={() => resolving && resolve.mutate({ id: resolving.id, resolution: resolving.resolution, notes: resolveNotes })}>
                {t("orderDetail.confirm", "Confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audit log */}
        <section>
          <h3 className="text-sm font-medium mb-2">
            {t("orderDetail.auditLog", "Audit log")} {isAdmin && <span className="text-xs text-muted-foreground">{t("orderDetail.auditLogAdmin", "(admin view)")}</span>}
          </h3>
          <div className="rounded-md border border-border/60 divide-y divide-border/60 text-xs">
            {(audit ?? []).length === 0 ? (
              <div className="p-3 text-muted-foreground">{t("orderDetail.noEvents", "No events yet.")}</div>
            ) : (audit ?? []).map((a) => (
              <div key={a.id} className="p-3 grid grid-cols-[1fr_auto] gap-y-1">
                <span className="font-mono">{a.event_type}</span>
                <span className="text-muted-foreground text-right">
                  {new Date(a.created_at).toLocaleString("en-GB")}
                </span>
                {a.amount_pence != null && (
                  <span className="text-muted-foreground col-span-2">
                    {t("orderDetail.amount", "Amount: {amount}", { amount: formatGBP(a.amount_pence) })}
                  </span>
                )}
                {a.metadata && Object.keys(a.metadata as object).length > 0 && (
                  <pre className="col-span-2 text-muted-foreground bg-surface-2/60 p-2 rounded overflow-x-auto">
                    {JSON.stringify(a.metadata, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </section>

        {order.stripe_checkout_url && order.status === "pending_payment" && (
          <a href={order.stripe_checkout_url} target="_blank" rel="noreferrer"
             className="text-sm text-gold inline-flex items-center gap-1">
            {t("orderDetail.resumeCheckout", "Resume Stripe checkout")} <ExternalLink className="h-3 w-3" />
          </a>
        )}
        <div>
          <Link
            to="/auction/$slug"
            params={{
              slug: order.auctions?.title
                ? buildAuctionSlug({ id: order.auction_id, title: order.auctions.title })
                : order.auction_id,
            }}
            className="text-xs text-muted-foreground hover:text-gold"
          >
            {t("orderDetail.viewAuction", "View auction")}
          </Link>
        </div>
      </div>
    </>
  );
}
